import React from "react";
import "./Inventory.scss";
import "devextreme/dist/css/dx.common.css";
import "devextreme/dist/css/dx.light.css";

import DataGrid, {
  Export,
  Column,
  Pager,
  Paging,
  FilterRow,
  HeaderFilter,
  Scrolling,
  Editing,
  NumericRule,
} from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";
import { ROLE_SALON_ADMIN, ROLE_SYSTEM_ADMIN } from "../../utils/constants";

// const URL = `gateway/cy3s87499h`
const URL = "gateway/dh6zwk9ifl"; //added logic to update unit price

function adjustToLocalTime(data) {
  var formattedTime = data.value;
  if (new Date(data.value).getYear() < 100) {
    return ``;
  }
  formattedTime = new Date(data.value).toLocaleString(`en-us`, {
    year: `2-digit`,
    month: `numeric`,
    day: `numeric`,
    hour: `numeric`,
    minute: `2-digit`,
  });

  return `${formattedTime}`;
}

function numericFormat(data) {
  let formattedNum = data.value;
  if (isNaN(formattedNum)) {
    return data.value;
  }
  return `${formattedNum}`;
}

function addDollarSign(data) {
  let formattedNum = data.value;
  if (isNaN(formattedNum)) {
    return data.value;
  }
  if (formattedNum < 0) {
    formattedNum = 0;
  }
  return `$${formattedNum}`;
}

async function sendBatchRequest(url, changes) {
  const result = await baseAxios.post(url, { changes });

  if (result.status !== 200) {
    const json = result.data;
    throw json.Message;
  }
}

async function processBatchRequest(url, changes, component) {
  await sendBatchRequest(url, changes);

  await component.refresh(true);

  component.cancelEditData();
}

export default function Inventory(props) {
  const { location_name } = props?.location?.state || {};

  //const api = 'gateway/l15igme5r0'; //Uses CurrentInventoryQuantityAPI
  // const api = `gateway/0ajbwl8yzg`; //Uses Development-SQL-CurrentInventoryAPI
  const api = "gateway/lz5hw2wipk"; //clone of Development-SQL-CurrentInventoryAPI (added unitary price per product)

  const { user } = useAuth();

  let userCredentials = {
    Barcode: user.barcode,
    Role: user.role,
    Organization: user.org,
    Location: user.location_id,
  };

  const dataSource = new CustomStore({
    load: function () {
      return baseAxios.post(api, userCredentials).then((result) => {
        result.data.forEach((item) => {
          item.inventory_current_quantity = (
            Math.round(item.inventory_current_quantity * 100) / 100
          ).toFixed(2);
          item.inventory_desired_quantity = (
            Math.round(item.inventory_desired_quantity * 100) / 100
          ).toFixed(2);
          item.inventory_on_hand = (
            Math.round(item.inventory_on_hand * 100) / 100
          ).toFixed(2);
          item.inventory_quantity_needed = (
            Math.round(item.inventory_quantity_needed * 100) / 100
          ).toFixed(2);
          item.inventory_product_price = (
            Math.round(item.inventory_product_price * 100) / 100
          ).toFixed(2);
        });

        return result.data;
      });
    },
  });

  const onSaving = React.useCallback((e) => {
    e.cancel = true;

    if (e.changes.length) {
      e.promise = processBatchRequest(URL, e.changes, e.component);
    }
  }, []);

  const showProductPriceColumn = user.userRoles.some(
    (x) => x === ROLE_SYSTEM_ADMIN || x === ROLE_SALON_ADMIN
  );

  return (
    <React.Fragment>
      <h2 className={"content-block"}>Current Inventory</h2>
      <div className={"content-block"}>
        <DataGrid
          dataSource={dataSource}
          className={"dx-card wide-card"}
          showBorders={true}
          defaultFocusedRowIndex={0}
          columnAutoWidth={true}
          columnHidingEnabled={false}
          allowColumnResizing={true}
          onSaving={onSaving}
        >
          <Paging defaultPageSize={25} />
          <Pager showPageSizeSelector={true} showInfo={true} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Editing
            mode="batch"
            allowUpdating={true}
            allowAdding={false}
            allowDeleting={false}
          ></Editing>
          <Scrolling mode="standard" />

          <Column
            allowEditing={false}
            dataField={"location_name"}
            caption={"Location"}
            filterValue={location_name || ""}
          />
          <Column
            allowEditing={false}
            dataField={"product_name"}
            caption={"Item"}
            sortOrder={"asc"}
          />
          <Column
            allowEditing={false}
            dataField={`manufacturer_name`}
            caption={"Manufacturer"}
          />
          <Column
            allowEditing={false}
            dataField={`brand_name`}
            caption={`Brand`}
          />
          <Column
            allowEditing={false}
            dataField={`category_name`}
            caption={`Category`}
          />

          <Column
            dataField={"inventory_current_quantity"}
            caption={"Item Count"}
            dataType="number"
            customizeText={numericFormat}
          >
            <NumericRule />
          </Column>
          <Column
            dataField={"inventory_desired_quantity"}
            caption={`Max Count`}
            dataType="number"
          >
            <NumericRule />
          </Column>

          <Column
            allowEditing={false}
            dataField={`inventory_quantity_needed`}
            caption={`Order Quantity`}
            dataType="number"
            customizeText={numericFormat}
          >
            <NumericRule />
          </Column>

          <Column
            allowEditing={false}
            dataField={"inventory_on_hand"}
            caption={`Value on Hand`}
            dataType="number"
            customizeText={addDollarSign}
          >
            <NumericRule />
          </Column>

          {showProductPriceColumn && (
            <Column
              dataField={"inventory_product_price"}
              caption={"Unit Price"}
              dataType="number"
              customizeText={addDollarSign}
            />
          )}
          {/* <Column
            dataField={"inventory_last_changed_user_id"}
            hidingPriority={8}
            alignment={"left"}
          /> */}
          <Column
            allowEditing={false}
            dataField={"inventory_last_changed_timestamp"}
            caption={"Last Ordered"}
            customizeText={adjustToLocalTime}
          />
          <Column
            allowEditing={false}
            dataField={"inventory_notes"}
            hidingPriority={6}
            caption={"Notes"}
          />
          <Column
            allowEditing={false}
            dataField={"product_upc_code"}
            hidingPriority={7}
            caption={"Barcode"}
          />

          <Export enabled={true} allowExportSelectedData={false} />
        </DataGrid>
      </div>
    </React.Fragment>
  );
}
